import { api } from 'aws-blocks';
import { useEffect, useState } from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { useAuthContext } from '../contexts/AuthContext';
import useApiAction from '../hooks/use-api-action';
import type { UserData } from '../types/app';
import Modal from './Modal';
import BkButton from './ui/BkButton';

const DISPLAY_NAME = 'displayName';

export const USER_FIELD_VALUES = {
  DISPLAY_NAME,
};

export type UpdateUserModalProps = {
  show: boolean;
  onClose: () => void;
  fields?: string[];
  onComplete?: (userInfo: UserData) => void;
};

export default function UpdateUserModal({
  show,
  onClose,
  fields = [],
  onComplete = () => {},
}: UpdateUserModalProps) {
  if (!fields.length) throw Error("UpdateUserModal needs values in it's fields prop.");

  const { userData } = useAuthContext();
  const [userInfoInput, setUserInfoInput] = useState<Partial<UserData>>({});
  const [updateError, setUpdateError] = useState('');
  const [updateUser, updating] = useApiAction({
    action: async (payload: { displayName?: string }) =>
      api.updateUser(String(payload.displayName)),
    reloadUserData: true,
  });

  useEffect(() => {
    if (!show) {
      setUserInfoInput({});
      setUpdateError('');
    }
  }, [show]);

  const userInfo: UserData = {
    ...userData,
    ...userInfoInput,
  };

  async function handleSave() {
    try {
      const next = (await updateUser({
        displayName: String(userInfo.displayName || ''),
      })) as UserData;
      onComplete(next || userInfo);
      onClose();
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Update failed');
    }
  }

  return (
    <Modal show={show} onClose={onClose} title="Update profile">
      <div className="space-y-3 p-2">
        {fields.includes(DISPLAY_NAME) && (
          <label className="block space-y-1">
            <span className="text-xs uppercase tracking-wide text-bk-muted">Display name</span>
            <input
              className="w-full border border-bk-border bg-bk-bg px-3 py-2 text-bk-text focus:border-bk-gold focus:outline-none focus:ring-1 focus:ring-bk-gold"
              value={String(userInfo.displayName || '')}
              onChange={(e) =>
                setUserInfoInput((prev) => ({ ...prev, displayName: e.target.value }))
              }
            />
          </label>
        )}
        {updateError && <p className="text-sm text-bk-danger-bright">{updateError}</p>}
        <BkButton
          variant="primary"
          disabled={updating}
          className="gap-2"
          onClick={() => void handleSave()}
        >
          {updating && <AiOutlineLoading3Quarters className="animate-spin" />}
          Save
        </BkButton>
      </div>
    </Modal>
  );
}
